/**
 * UserModeContext (iter76)
 *
 * Global "User Mode" toggle:
 *   · simple   — Apple-style tile launcher, hidden sidebar. Default for
 *                first-time students.
 *   · advanced — the full sidebar app. Always active for coaches + admins,
 *                regardless of what's on their user doc.
 *
 * Persisted on the user profile via POST /api/me/user-mode + localStorage
 * for instant switching.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
const STORAGE_KEY = "rally_user_mode";
const VALID = new Set(["simple", "advanced"]);
const DEFAULT_MODE = "simple";
// Coaches + admins always use the full sidebar experience — bypass the
// stored user_mode so they never accidentally get boxed into a 4-tile
// launcher.
const ROLES_FORCED_ADVANCED = new Set(["coach", "admin"]);

function loadInitial() {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : DEFAULT_MODE;
  } catch { return DEFAULT_MODE; }
}

export function UserModeProvider({ children }) {
  const { user } = useAuth();
  const [rawMode, setRawMode] = useState(loadInitial);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    if (!user) return;
    const pref = user.user_mode;
    if (pref && VALID.has(pref)) {
      hydrated.current = true;
      setRawMode(pref);
      try { window.localStorage.setItem(STORAGE_KEY, pref); } catch { /* ignore */ }
    } else {
      hydrated.current = true;
    }
  }, [user]);

  // If the role forces advanced we swap the effective mode transparently.
  const isForced = user?.role && ROLES_FORCED_ADVANCED.has(user.role);
  const mode = isForced ? "advanced" : rawMode;

  const setMode = useCallback(async (next) => {
    if (!VALID.has(next)) return;
    setRawMode(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    try { await api.post("/me/user-mode", { mode: next }); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === "simple" ? "advanced" : "simple");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, setMode, toggle, isForced, isSimple: mode === "simple" }),
    [mode, setMode, toggle, isForced],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUserMode() {
  const ctx = useContext(Ctx);
  if (!ctx) return { mode: "advanced", setMode: () => {}, toggle: () => {}, isForced: false, isSimple: false };
  return ctx;
}
